/**
 * A confirmação de uma ação que gasta dinheiro ou manda dado pessoal para fora.
 *
 * Existe como componente, e não como um `window.confirm`, por três razões que
 * não são estéticas:
 *
 * 1. **`confirm()` nativo não é UI.** Ele não mostra número, não mostra tabela,
 *    não dá para ler com leitor de tela do jeito que o resto do painel é lido, e
 *    aparece como um popup do navegador em cima de um produto que cobra dinheiro;
 *
 * 2. **a confirmação diz NÚMEROS, não adjetivos.** "Confirma o envio?" não é
 *    confirmação de nada: quem clica precisa ver quantos contatos vão, quantos
 *    ficam de fora, quanto sai do envelope e quanto sobra. O tipo obriga: a
 *    propriedade `numeros` é uma tupla NÃO VAZIA, então não existe como usar
 *    este componente sem dizer ao menos um número. Um `readonly Linha[]` comum
 *    aceitaria uma lista vazia, e a primeira tela apressada passaria `[]`;
 *
 * 3. **os números vêm do SERVIDOR, já formatados.** O componente só desenha. Se
 *    ele formatasse dinheiro, existiriam duas formatações no produto, e elas
 *    divergiriam.
 */

export type LinhaDaConfirmacao = {
  readonly rotulo: string;
  /** Já formatado pelo servidor. A tela não faz conta de dinheiro. */
  readonly valor: string;
  /** Um detalhe curto, quando o número sozinho não basta. */
  readonly detalhe?: string;
};

export type ConfirmacaoProps = {
  readonly titulo: string;
  /** O que vai acontecer, em uma frase, no tempo futuro. */
  readonly oQueVaiAcontecer: string;
  /**
   * Os números da decisão. Tupla não vazia: pelo menos um é obrigatório.
   */
  readonly numeros: readonly [LinhaDaConfirmacao, ...LinhaDaConfirmacao[]];
  readonly textoDoBotao: string;
  readonly aoConfirmar: () => void;
  readonly aoCancelar: () => void;
  readonly ocupado?: boolean;
  /** O que impede de confirmar, quando algo impede. */
  readonly impedimento?: string | null;
};

export function Confirmacao({
  titulo,
  oQueVaiAcontecer,
  numeros,
  textoDoBotao,
  aoConfirmar,
  aoCancelar,
  ocupado = false,
  impedimento = null,
}: ConfirmacaoProps) {
  return (
    <section
      className="mt-3 rounded border border-warn-border bg-warn-surface p-4"
      aria-labelledby="titulo-da-confirmacao"
      data-testid="confirmacao"
    >
      <h3 id="titulo-da-confirmacao" className="text-sm font-semibold">
        {titulo}
      </h3>
      <p className="mt-1 text-sm">{oQueVaiAcontecer}</p>

      <dl className="mt-3 space-y-1 text-sm">
        {numeros.map((n) => (
          <div key={n.rotulo} className="flex flex-wrap items-baseline gap-2">
            <dt className="text-ink-muted">{n.rotulo}:</dt>
            <dd>
              <strong>{n.valor}</strong>
              {n.detalhe === undefined ? null : (
                <span className="ml-1 text-xs text-ink-muted">({n.detalhe})</span>
              )}
            </dd>
          </div>
        ))}
      </dl>

      {impedimento === null ? null : (
        <p className="mt-3 rounded border border-danger-border bg-danger-surface p-2 text-sm">
          {impedimento}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={aoConfirmar}
          disabled={ocupado || impedimento !== null}
          className="rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          {textoDoBotao}
        </button>
        <button
          type="button"
          onClick={aoCancelar}
          disabled={ocupado}
          className="rounded border border-line-strong px-3 py-1 text-sm hover:bg-surface-raised disabled:opacity-50"
        >
          Cancelar
        </button>
      </div>
    </section>
  );
}
